import {
  ARCFACE_LANDMARK_TEMPLATE,
  AntiSpoof,
  Detector,
  Embedder,
  Preprocessor,
  QualityGate,
  cropAndResize,
  defaultConfig,
  estimateSimilarityTransform,
  warpAffine,
} from './vendor/index.js';
import type { Bbox, FaceDetection, ImageFrame, Landmarks5, QualityReport } from './vendor/index.js';
import { faceModelUrls } from './faceModelUrls.js';
import { onnxRuntimeBootstrap } from './onnxRuntimeBootstrap.js';
import {
  ModelLoadError,
} from './errors.js';
import type { DetectionHandle, QualityRuntimeConfig } from './types.js';
import { mergeRuntimeConfig } from './mergeRuntimeConfig.js';
import { downscaleForDetection } from './downscaleForDetection.js';
import { capturedFetch } from '@/shared/devtools/capturedFetch';

// ----- types -----------------------------------------------------------------

export type InferEmbeddingResult =
  | { kind: 'embedding'; value: number[]; livenessScore: number }
  | { kind: 'liveness-pending'; livenessScore: number }
  | { kind: 'low-quality'; subreason: 'blur' | 'brightness' | 'size'; report: QualityReport };

// ----- helpers ---------------------------------------------------------------

const _modelCache = new Map<string, Uint8Array>();

async function fetchModelBytes(url: string): Promise<Uint8Array> {
  const cached = _modelCache.get(url);
  if (cached) return cached;

  const res = await capturedFetch(url, { cache: 'force-cache' });
  if (!res.ok) throw new ModelLoadError(`Failed to fetch model ${url}: HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  if (buf.byteLength === 0) throw new ModelLoadError(`Model file is empty: ${url}`);
  const bytes = new Uint8Array(buf);
  _modelCache.set(url, bytes);
  return bytes;
}

function l2Normalize(arr: number[]): number[] {
  let norm = 0;
  for (const v of arr) norm += v * v;
  norm = Math.sqrt(norm);
  if (norm < 1e-12) throw new ModelLoadError('L2 norm collapsed — embedding is degenerate');
  return arr.map((v) => v / norm);
}

// ----- OnnxFaceMatcher (singleton) -------------------------------------------

export class OnnxFaceMatcher {
  private static _instance: OnnxFaceMatcher | null = null;

  static getInstance(): OnnxFaceMatcher {
    return (this._instance ??= new OnnxFaceMatcher());
  }

  private readonly cfg = defaultConfig();
  private detector?: Detector;
  private embedder?: Embedder;
  private antiSpoof?: AntiSpoof;
  private quality?: QualityGate;
  private preprocessor?: Preprocessor;
  private _antiSpoofAvailable = false;
  private _ready = false;
  private _initPromise: Promise<void> | null = null;
  /** Wall-clock duration (ms) of the dummy SCRFD inference fired during _prewarm(). */
  private _prewarmMs: number | undefined = undefined;

  isReady(): boolean { return this._ready; }
  hasAntiSpoof(): boolean { return this._antiSpoofAvailable; }

  /**
   * Returns the wall-clock time (ms) of the dummy SCRFD inference fired during
   * pre-warm, or `undefined` if pre-warm has not completed (or was never run).
   *
   * Used by `DevicePerfProfile.classifyTier()` to determine the detection
   * downscale factor for this device.
   */
  getPrewarmMs(): number | undefined { return this._prewarmMs; }

  /**
   * Initialize ONNX models. Idempotent and concurrent-safe:
   * - If models are already ready, returns a resolved Promise immediately.
   * - If initialization is in progress, returns the SAME Promise instance (not a new wrapper).
   * - If initialization failed previously, resets and allows a fresh retry.
   *
   * IMPORTANT: Must NOT be declared `async` — the `async` keyword wraps each return value in a
   * new Promise, breaking reference equality when two concurrent callers check `promise1 === promise2`.
   */
  initialize(): Promise<void> {
    if (this._ready) return Promise.resolve();
    if (this._initPromise) return this._initPromise;
    this._initPromise = this._doInit().catch((err) => {
      this._initPromise = null; // allow retry on next call
      throw err;
    });
    return this._initPromise;
  }

  private async _doInit(): Promise<void> {
    // Wire the ONNX runtime once
    onnxRuntimeBootstrap();

    // Give the lazy-import inside onnxRuntimeBootstrap time to resolve
    // (it's fire-and-forget, so we wait for getRuntime() to be populated).
    await this._waitForRuntime();

    this.cfg.detector.modelPath  = faceModelUrls.scrfd;
    this.cfg.embedder.modelPath  = faceModelUrls.mobilefacenet;
    this.cfg.antispoof.modelPath = faceModelUrls.minifasnetv2;

    this.detector    = new Detector(this.cfg.detector);
    this.embedder    = new Embedder(this.cfg.embedder);
    this.quality     = new QualityGate(this.cfg.quality);
    this.preprocessor = new Preprocessor(this.cfg);

    // Load core models in parallel
    const [scrfdBytes, mobBytes] = await Promise.all([
      fetchModelBytes(faceModelUrls.scrfd),
      fetchModelBytes(faceModelUrls.mobilefacenet),
    ]);
    await Promise.all([
      this.detector.load(scrfdBytes),
      this.embedder.load(mobBytes),
    ]);

    // Anti-spoof: optional — load only if model file exists
    try {
      const antiSpoofBytes = await fetchModelBytes(faceModelUrls.minifasnetv2);
      this.antiSpoof = new AntiSpoof(this.cfg.antispoof);
      await this.antiSpoof.load(antiSpoofBytes);
      this._antiSpoofAvailable = true;
    } catch {
      // Model not yet available (requires PyTorch conversion) — proceed without it
      console.warn(
        '[OnnxFaceMatcher] MiniFASNetV2 not available — liveness check disabled. ' +
        'Run: python models/scripts/convert_to_onnx.py from-pytorch ...',
      );
    }

    this._ready = true;
    void this._prewarm();
  }

  /**
   * Fire one dummy inference to spin up the ORT web-worker pool immediately after
   * model loading. This amortises the first-frame latency by initialising the worker
   * while the user is still navigating to the scan screen.
   *
   * Errors are intentionally swallowed: a pre-warm failure is non-fatal and must NOT
   * block the boot sequence or affect the caller.
   */
  private async _prewarm(): Promise<void> {
    // Defensive: pre-warm runs after `_ready = true`, where `detector` must be set.
    // The guard makes the implicit contract explicit and prevents a TypeError that
    // the catch block would otherwise swallow silently.
    if (!this.detector) return;
    try {
      const data = new Uint8ClampedArray(640 * 640 * 4); // zero RGBA — black frame
      const t0 = performance.now();
      await this.detector.detect({ data, width: 640, height: 640, format: 'rgba' });
      // Store the measured pre-warm duration so callers can classify the device tier.
      this._prewarmMs = performance.now() - t0;
    } catch (err) {
      // Logged (not silent) so a pre-warm regression is visible in production logs.
      // Behaviour is still non-fatal: pre-warm failure must NOT block the caller.
      console.warn('[OnnxFaceMatcher] pre-warm failed:', err);
    }
  }

  private async _waitForRuntime(timeoutMs = 5000): Promise<void> {
    const { getRuntime } = await import('./vendor/platform/runtime.js');
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      try {
        getRuntime();
        return;
      } catch {
        await new Promise((r) => setTimeout(r, 50));
      }
    }
    throw new ModelLoadError('ONNX runtime not registered after timeout');
  }

  /**
   * Gate B: preprocess → quality (with runtime config) → [liveness score] → align → embed.
   * 
   * @param frame Full-resolution frame (same frame passed to analyzeQuality).
   * @param detection DetectionHandle from Gate A — bbox/landmarks in full-res coords.
   * @param runtimeConfig Optional runtime quality overrides (merged consumer-side).
   * 
   * Returns:
   * - { kind: 'embedding', value, livenessScore } — quality passed, embedding extracted, liveness score computed
   * - { kind: 'liveness-pending', livenessScore } — NOT USED by this method (aggregator decides in caller)
   * - { kind: 'low-quality', subreason, report } — quality failed at Gate B
   * 
   * Throws:
   * - EmbeddingTimeoutError (via withTimeout wrapper in caller)
   * - ModelLoadError (models not initialized)
   * 
   * NO LONGER THROWS:
   * - NoFaceDetectedError / MultipleFacesError (Gate A exclusive)
   * - LivenessBorderlineError (replaced by aggregator pending state)
   */
  async inferEmbedding(
    frame: ImageFrame,
    detection: DetectionHandle,
    runtimeConfig?: QualityRuntimeConfig,
  ): Promise<InferEmbeddingResult> {
    if (!this._ready || !this.embedder || !this.quality || !this.preprocessor) {
      throw new ModelLoadError('OnnxFaceMatcher not initialized — call initialize() first');
    }

    // Preprocess full-res frame
    const { frame: pre } = this.preprocessor.apply(frame);

    // Gate B quality evaluation with runtime config
    const mergedConfig = mergeRuntimeConfig(this.cfg.quality, runtimeConfig);
    // Instantiate temporary QualityGate with merged config (vendor evaluate doesn't accept config param)
    const qualityGate = runtimeConfig ? new QualityGate(mergedConfig) : this.quality;
    const q = qualityGate.evaluate(pre, detection);

    // Quality gate: classify subreason (first failure wins)
    if (!q.blurOk) {
      return { kind: 'low-quality', subreason: 'blur', report: q };
    }
    if (!q.brightnessOk) {
      return { kind: 'low-quality', subreason: 'brightness', report: q };
    }
    if (!q.sizeOk) {
      return { kind: 'low-quality', subreason: 'size', report: q };
    }

    // Compute liveness score (no verdict decision here — caller aggregates)
    let livenessScore = 1.0; // assume live if anti-spoof unavailable
    if (this._antiSpoofAvailable && this.antiSpoof) {
      const scale = this.antiSpoof.cropScale; // 2.7 default
      const [x1, y1, x2, y2] = detection.bbox;
      const cxn = (x1 + x2) / 2;
      const cyn = (y1 + y2) / 2;
      const hw  = ((x2 - x1) * scale) / 2;
      const hh  = ((y2 - y1) * scale) / 2;
      const crop80 = cropAndResize(
        pre,
        Math.max(0, cxn - hw), Math.max(0, cyn - hh),
        Math.min(pre.width, cxn + hw), Math.min(pre.height, cyn + hh),
        80, 80,
      );
      const liveness = await this.antiSpoof.score(crop80);
      livenessScore = liveness.score;
      // NO single-frame verdict — caller aggregates
    }

    // Align and extract embedding
    const M = estimateSimilarityTransform(detection.landmarks, ARCFACE_LANDMARK_TEMPLATE);
    const aligned = warpAffine(pre, M, 112, 112);
    const rawVec  = await this.embedder.embed(aligned);
    const embedding = l2Normalize(Array.from(rawVec));

    return { kind: 'embedding', value: embedding, livenessScore };
  }

  /**
   * Gate A: lightweight quality analysis (preprocess → detect → quality gate only).
   * Does NOT compute embedding or run anti-spoof.
   * Designed for real-time feedback during enrollment and the quality gate loop.
   *
   * @param frame         Full-resolution source frame. Always passed unchanged to
   *                      `inferEmbedding` — this function never modifies the caller's frame.
   * @param scaleHint     Detection-only downscale factor from `DevicePerfProfile.detectionScaleForTier`.
   *                      Defaults to 1.0 (no downscale). Values < 1 cause SCRFD to run on a
   *                      smaller frame for CPU efficiency; bbox / landmark coordinates are
   *                      rescaled back to full-res space before quality evaluation so that
   *                      `faceShortSidePx` stays comparable to the threshold.
   *                      ⚠️ This scale hint applies to SCRFD detection ONLY — `inferEmbedding`
   *                      must always receive the original full-resolution frame.
   * @param runtimeConfig Optional runtime quality overrides (merged consumer-side per design D1).
   *
   * Returns:
   * - `detection`: DetectionHandle with full-res coords when exactly one face detected (null otherwise).
   * - `durationMs`: wall-clock time (ms) for downscale + SCRFD detect call (0 in early-return paths).
   */
  async analyzeQuality(
    frame: ImageFrame,
    scaleHint = 1.0,
    runtimeConfig?: QualityRuntimeConfig,
  ): Promise<{
    ok: boolean;
    quality: QualityReport | null;
    hasOneFace: boolean;
    detection: DetectionHandle | null;
    reasons: string[];
    durationMs: number;
  }> {
    if (!this._ready || !this.detector || !this.quality || !this.preprocessor) {
      return { ok: false, quality: null, hasOneFace: false, detection: null, reasons: ['Models not initialized'], durationMs: 0 };
    }

    try {
      // ── Detection phase ──────────────────────────────────────────────────
      // Optionally downscale the frame for SCRFD efficiency (the 80% of CPU cost).
      // Guard: skip the downscale call entirely when scale >= 1.0 — avoids a
      // module-level function call (and an unnecessary canvas alloc) on fast devices.
      const t0             = performance.now();
      const detFrame       = scaleHint < 1.0 ? downscaleForDetection(frame, scaleHint) : frame;
      const { frame: preDet } = this.preprocessor.apply(detFrame);
      const detections     = await this.detector.detect(preDet);
      const durationMs     = performance.now() - t0;

      if (detections.length === 0) {
        return { ok: false, quality: null, hasOneFace: false, detection: null, reasons: ['No face detected'], durationMs };
      }
      if (detections.length > 1) {
        return { ok: false, quality: null, hasOneFace: false, detection: null, reasons: [`${detections.length} faces — ensure only one face is visible`], durationMs };
      }

      // Rescale SCRFD output to full-res coordinates when a downscale was applied.
      // OQ-1 resolution: preprocessor is dimension-preserving (no letterbox) →
      // simple division by scaleHint correctly maps downscaled → full-res coords.
      const rawDet = detections[0]!;
      const det    = scaleHint < 1.0 ? this._rescaleDetection(rawDet, scaleHint) : rawDet;

      // Build DetectionHandle for Gate B (full-res coords)
      const detectionHandle: DetectionHandle = {
        bbox: det.bbox,
        landmarks: det.landmarks,
        score: det.score,
      };

      // ── Quality evaluation phase with runtime config ─────────────────────
      // Merge runtime config with vendor defaults
      const mergedConfig = mergeRuntimeConfig(this.cfg.quality, runtimeConfig);
      // Instantiate temporary QualityGate with merged config (vendor evaluate doesn't accept config param)
      const qualityGate = runtimeConfig ? new QualityGate(mergedConfig) : this.quality;
      
      // Hotfix reverting PR#4 H1 from the original judgment-day review.
      //
      // The H1 fix moved quality.evaluate to the FULL-RES preprocessed frame
      // (correct in theory: keeps Laplacian-variance calibration consistent
      // across tiers). On mobile WebView (Redmi 12C / Samsung A12) the second
      // preprocessor.apply() on the 640×480 frame — which runs gamma + CLAHE
      // in pure JS — pushed analyzeQuality from ~300ms to >1300ms per frame,
      // killing the scan loop entirely.
      //
      // Restore the original PR#4 behaviour:
      //   - quality.evaluate runs on the downscaled preprocessed frame `preDet`
      //   - using the raw (downscaled-coords) bbox `rawDet`
      //   - faceShortSidePx is then post-scaled back to full-res via
      //     _rescaleQualityReport so the downstream sizeOk threshold check
      //     keeps using full-res pixel counts.
      //
      // Known tradeoff (was H1 of the review): Laplacian variance does not
      // scale linearly with resolution. Slow-tier devices will see different
      // blur values than fast-tier for the same face. The user's matchThreshold
      // is ≤0.85 with headroom; empirical mobile logs (blur=251 / 340) stayed
      // far above the 120 threshold, so no false-reject regression observed.
      const q = qualityGate.evaluate(preDet, scaleHint < 1.0 ? rawDet : det);
      const quality = scaleHint < 1.0 ? this._rescaleQualityReport(q, scaleHint, mergedConfig) : q;

      return { ok: quality.ok, quality, hasOneFace: true, detection: detectionHandle, reasons: quality.reasons, durationMs };
    } catch {
      return { ok: false, quality: null, hasOneFace: false, detection: null, reasons: ['Analysis error'], durationMs: 0 };
    }
  }

  /**
   * Post-correct a QualityReport whose bbox came from a downscaled frame.
   * `faceShortSidePx` is scaled up to full-res; `sizeOk` is recomputed against
   * the merged quality config threshold (runtime overrides applied) so the enrollment
   * gate sees correct pixel counts with unified thresholds.
   *
   * Restored as the mobile-perf hotfix for PR#4 H1 (see analyzeQuality comment).
   */
  private _rescaleQualityReport(q: QualityReport, scaleHint: number, mergedConfig: { faceMinShortSidePx: number }): QualityReport {
    const inv             = 1 / scaleHint;
    const faceShortSidePx = Math.floor(q.faceShortSidePx * inv);
    const sizeOk          = faceShortSidePx >= mergedConfig.faceMinShortSidePx;
    const reasons         = sizeOk
      ? q.reasons.filter((r) => !r.startsWith('face too small'))
      : q.reasons;
    const ok              = q.blurOk && q.brightnessOk && sizeOk && q.poseOk;
    return { ...q, faceShortSidePx, sizeOk, ok, reasons };
  }

  /**
   * Rescale SCRFD bbox and landmarks from downscaled → full-res coordinates.
   * OQ-1: preprocessor has no letterbox → simple `coord / scaleHint` is correct.
   */
  private _rescaleDetection(det: FaceDetection, scaleHint: number): FaceDetection {
    const inv = 1 / scaleHint;
    const bbox: Bbox = [
      det.bbox[0] * inv,
      det.bbox[1] * inv,
      det.bbox[2] * inv,
      det.bbox[3] * inv,
    ];
    const landmarks = det.landmarks.map(([x, y]) => [x * inv, y * inv] as const) as unknown as Landmarks5;
    return { bbox, score: det.score, landmarks };
  }
}
